import React, { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import Legend from "../Legend";
import { HoverState, InteractiveLineChartData, SelectionState } from "../types";
import {
  formatTimeLabel,
  tooltipFormatter,
  processTooltipItems,
  findMaxValue,
} from "../utils";

import "./styles.scss";

interface ChartProps<T = string> {
  data: InteractiveLineChartData<T>;
  width?: number | string;
  height?: number | string;
  xAxisDataKey?: string;
  yAxisLabel?: string;
  tooltipFormatter?: (value: any, name: string) => React.ReactNode;
  onResetSelectionState?: (resetFn: () => void) => void;
  chartContainerId?: string;
}

const Chart = <T extends string>({
  data,
  width = "100%",
  height = 400,
  xAxisDataKey = "timestamp",
  yAxisLabel = "",
  onResetSelectionState,
  chartContainerId,
}: ChartProps<T>) => {
  const [hoverState, setHoverState] = useState<HoverState>({
    activeCategory: null,
    activeLabel: null,
  });

  const [selectionState, setSelectionState] = useState<SelectionState>({
    selectedLabels: new Set<string>(),
    selectedCategories: new Set<string>(),
    hasSelection: false,
  });

  const maxDataValue = useMemo(() => {
    return findMaxValue(data);
  }, [data]);

  const resetSelectionState = React.useCallback(() => {
    setSelectionState({
      selectedLabels: new Set<string>(),
      selectedCategories: new Set<string>(),
      hasSelection: false,
    });
  }, []);

  React.useEffect(() => {
    if (onResetSelectionState) {
      onResetSelectionState(resetSelectionState);
    }
  }, [onResetSelectionState, resetSelectionState]);

  React.useEffect(() => {
    resetSelectionState();
  }, [data, resetSelectionState]);

  const TooltipContent = (props: any) => {
    if (!props.active || !props.payload || props.payload.length === 0) {
      return null;
    }

    const timestamp = props.label;
    const formattedTime = formatTimeLabel(timestamp);

    const visibleItems = processTooltipItems(
      props.payload,
      timestamp,
      data,
      hoverState,
      selectionState
    );

    if (visibleItems.length === 0) {
      return null;
    }

    return (
      <div className="interactive-line-chart-tooltip">
        <p className="tooltip-time">{formattedTime}</p>
        <div className="tooltip-items">
          {visibleItems.map((item: any) => {
            return (
              <div key={`tooltip-${item.key}`} className="tooltip-item">
                <div
                  className="color-indicator"
                  style={{ backgroundColor: item.color }}
                />
                <span className="keyword">{item.label}</span>
                <span className="value">
                  {tooltipFormatter ? tooltipFormatter(item.value) : item.value}
                </span>
                <span className="category">{item.categoryName}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const shouldShowItem = (category: string, label: string) => {
    if (selectionState.hasSelection) {
      const isLabelSelected = selectionState.selectedLabels.has(label);
      const isCategorySelected =
        selectionState.selectedCategories.has(category);

      if (hoverState.activeLabel === label) {
        return true;
      }

      if (selectionState.selectedLabels.size > 0) {
        return isLabelSelected;
      }

      if (selectionState.selectedCategories.size > 0) {
        return isCategorySelected;
      }
    }

    return !!(
      !(hoverState.activeCategory && hoverState.activeCategory !== category) &&
      !(hoverState.activeLabel && hoverState.activeLabel !== label)
    );
  };

  const isItemActive = (category: string, label: string) => {
    if (selectionState.hasSelection) {
      const isLabelSelected = selectionState.selectedLabels.has(label);
      const isCategorySelected =
        selectionState.selectedCategories.has(category);

      return (
        isLabelSelected ||
        (isCategorySelected && selectionState.selectedLabels.size === 0)
      );
    }

    return (
      (!hoverState.activeCategory && !hoverState.activeLabel) ||
      (hoverState.activeCategory === category &&
        (!hoverState.activeLabel || hoverState.activeLabel === label))
    );
  };

  const maxValue = maxDataValue;

  return (
    <div className="interactive-line-chart-container" id={chartContainerId}>
      <ResponsiveContainer width={width} height={height}>
        <LineChart
          data={data.timeSeriesData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey={xAxisDataKey}
            tickFormatter={formatTimeLabel}
            tick={{ fontSize: 12 }}
            style={{ fontFamily: "Inter" }}
          />
          <YAxis
            label={{
              value: yAxisLabel,
              angle: -90,
              position: "insideLeft",
            }}
            tick={{ fontSize: 12 }}
            tickFormatter={(value: number) =>
              new Intl.NumberFormat("ja").format(value)
            }
            domain={[0, maxValue]}
          />
          <Tooltip content={<TooltipContent />} />

          {data.categories.flatMap((category) =>
            category.items.map((item) => {
              const isActive = isItemActive(category.categoryName, item.label);
              const shouldShow = shouldShowItem(
                category.categoryName,
                item.label
              );

              return (
                <Line
                  key={`${item.key}-dots`}
                  type="monotone"
                  dataKey={`values.${item.key}.value`}
                  name={item.label}
                  stroke="transparent"
                  dot={{
                    r: isActive ? 4 : 3,
                    stroke: item.color,
                    strokeWidth: 1,
                    fill: item.color,
                  }}
                  activeDot={{ r: 6 }}
                  connectNulls={false}
                  isAnimationActive={true}
                  animationBegin={0}
                  animationDuration={300}
                  animationEasing="ease-out"
                  hide={!shouldShow}
                />
              );
            })
          )}

          {data.categories.flatMap((category) =>
            category.items.map((item) => {
              const isActive = isItemActive(category.categoryName, item.label);
              const shouldShow = shouldShowItem(
                category.categoryName,
                item.label
              );

              return (
                <Line
                  key={`${item.key}-lines`}
                  type="monotone"
                  dataKey={`values.${item.key}.value`}
                  name={item.label}
                  stroke={item.color}
                  strokeWidth={isActive ? 3 : 2}
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={true}
                  animationBegin={150}
                  animationDuration={600}
                  animationEasing="ease-in-out"
                  hide={!shouldShow}
                  activeDot={false}
                  style={{ strokeLinecap: "round", strokeLinejoin: "round" }}
                />
              );
            })
          )}
        </LineChart>
      </ResponsiveContainer>

      <Legend
        categories={data.categories}
        hoverState={hoverState}
        setHoverState={setHoverState}
        selectionState={selectionState}
        setSelectionState={setSelectionState}
      />
    </div>
  );
};

export default Chart;
